import { HttpResponse, ServiceClient } from '@johnlewispartnership/wtr-website-api-client';
import config from 'config';
import { logApiError, logApiInfo, LoggerRequestContext } from 'logger';

import {
  CustomerOrderStats,
  GetCustomerOrderStatsResult,
} from 'services/customerOrderOrchestration/types';
import getApiBaseUrl from 'utils/getApiBaseUrl';

const customerOrderOrchestrationApiUrl = getApiBaseUrl(config.services.customerOrders.path);
const serviceName = 'customerOrderOrchestration';

class CustomerOrderOrchestrationServiceClient extends ServiceClient {
  async getStats({
    authHeader,
    context,
  }: {
    authHeader: string;
    context?: LoggerRequestContext;
  }): Promise<GetCustomerOrderStatsResult> {
    const path = '/v1/customerorders/stats';
    const response = await this.httpClient
      .get<CustomerOrderStats>(path, {
        headers: {
          Authorization: authHeader,
        },
      })
      .catch(e => ({ error: e }) as HttpResponse<CustomerOrderStats>);

    const customerOrderStats = response.data;
    const customerOrdersStatsApiUrl = `${customerOrderOrchestrationApiUrl}${path}`;

    if (!customerOrderStats || response.error) {
      logApiError(
        {
          payload: {
            service: serviceName,
            path,
            url: customerOrdersStatsApiUrl,
            message: `Error getting customer order stats: ${
              response.error?.message || 'Customer order stats missing from response'
            }`,
            duration: response.duration,
            /**
             * This field has a different format according to our logging standards (e.g.: "200 (OK)" ).
             * It's a string value, which means we can't do queries like "httpStatus > 200 AND httpStatus < 300".
             * We might want to get the standards changed.
             * See logging standards: https://www.jlpit.com/wiki/pages/viewpage.action?spaceKey=WD&title=Waitrose+E-commerce+logging+standards
             */
            httpStatus: `${response.status}`,
            // This field is not documented in the logging standards, but it's a number, so we can use relational operators in our queries.
            httpStatusCode: response.status,
          },
        },
        context,
      );
    } else {
      logApiInfo(
        {
          payload: {
            service: serviceName,
            path,
            url: customerOrdersStatsApiUrl,
            message: 'Customer order stats loaded successfully',
            duration: response.duration,
            httpStatus: `${response.status}`,
            httpStatusCode: response.status,
          },
        },
        context,
      );
    }

    return {
      customerOrderStats: customerOrderStats || null,
      error: response.error ? { message: response.error.message } : undefined,
    };
  }
}

const customerOrderOrchestrationServiceClient = new CustomerOrderOrchestrationServiceClient(
  customerOrderOrchestrationApiUrl,
);

export default customerOrderOrchestrationServiceClient;
